/**
 * IQ Analytics (Nitel IQ): synapse action label IDs and display strings.
 *
 * Used across Historical Observations, Aggregated IQ dashboard, Metric Details (UNM),
 * Quality of Experience, ticket modals/exports, etc.
 */
export const IQ_ACTION_LABEL_DISPLAY = {
  OBSERVE: 'Observe',
  MONITOR: 'Monitor',
  INVESTIGATE: 'Investigate',
} as const;

export type IqActionLabelId = keyof typeof IQ_ACTION_LABEL_DISPLAY;

/** API / dashboard observation count field keys (lowercase). */
export type IqDashboardCountKey =
  | 'observe'
  | 'monitor'
  | 'investigate';

/** Investigate (most severe) → Observe; filters, UNM severity charts, etc. */
export const IQ_ACTION_LABEL_IDS_BY_SEVERITY = [
  'INVESTIGATE',
  'MONITOR',
  'OBSERVE',
] as const satisfies readonly IqActionLabelId[];

/** @deprecated Use {@link IQ_ACTION_LABEL_IDS_BY_SEVERITY}. */
export const IQ_ACTION_LABEL_IDS_HISTORICAL_ORDER =
  IQ_ACTION_LABEL_IDS_BY_SEVERITY;

/** Display strings in severity order (matches {@link IQ_ACTION_LABEL_IDS_BY_SEVERITY}). */
export const IQ_SEVERITY_DISPLAYS: readonly string[] =
  IQ_ACTION_LABEL_IDS_BY_SEVERITY.map(
    (id) => IQ_ACTION_LABEL_DISPLAY[id],
  );

export function getIqActionLabel(id: string): string {
  return (
    IQ_ACTION_LABEL_DISPLAY[id as IqActionLabelId] ?? id
  );
}

/**
 * Synapse / IQ severity values keyed for UNM, QoE, and APIs (`action_label`: Observe | Monitor | Investigate).
 * Single source for display strings; do not duplicate in feature models.
 */
export const IQ_SEVERITY_LEVELS = {
  Observe: IQ_ACTION_LABEL_DISPLAY.OBSERVE,
  Monitor: IQ_ACTION_LABEL_DISPLAY.MONITOR,
  Investigate: IQ_ACTION_LABEL_DISPLAY.INVESTIGATE,
} as const;

export type IqSeverityLevel =
  (typeof IQ_SEVERITY_LEVELS)[keyof typeof IQ_SEVERITY_LEVELS];

/** Aggregated IQ dashboard observation tiles (display + styling + count field). */
export interface IqTileDefinition {
  readonly actionLabelId: IqActionLabelId;
  readonly label: string;
  readonly accentBarClass: string;
  readonly accentTextClass: string;
  readonly countKey: IqDashboardCountKey;
}

const IQ_TILE_ORDER = [
  'OBSERVE',
  'MONITOR',
  'INVESTIGATE',
] as const satisfies readonly IqActionLabelId[];

const IQ_TILE_STYLES: Record<
  IqActionLabelId,
  Pick<IqTileDefinition, 'accentBarClass' | 'accentTextClass'>
> = {
  OBSERVE: {
    accentBarClass: 'bg-iq-observe',
    accentTextClass: 'text-iq-observe',
  },
  MONITOR: {
    accentBarClass: 'bg-iq-monitor',
    accentTextClass: 'text-iq-monitor',
  },
  INVESTIGATE: {
    accentBarClass: 'bg-iq-investigate',
    accentTextClass: 'text-iq-investigate',
  },
};

function actionLabelIdToDashboardCountKey(
  id: IqActionLabelId,
): IqDashboardCountKey {
  return id.toLowerCase() as IqDashboardCountKey;
}

export const IQ_TILE_DEFINITIONS: readonly IqTileDefinition[] =
  IQ_TILE_ORDER.map((actionLabelId) => ({
    actionLabelId,
    label: IQ_ACTION_LABEL_DISPLAY[actionLabelId],
    countKey: actionLabelIdToDashboardCountKey(actionLabelId),
    ...IQ_TILE_STYLES[actionLabelId],
  }));
